import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import classnames from 'clsx';
import { useNavigate } from 'react-router-dom-v5-compat';
import { faSparkles as faSparklesSolid } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mantine/core';

import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { Text, TextType } from 'ui-components/components/text';

import { getPackagesCustomSettingsPath } from '../../../../../../../routes/constants';
import { ValidationErrorType } from '../../../../../../../packing/utils/data-transformer';
import type { BlockingValidationErrorType } from '../../../../../../../packing/utils/data-transformer';

import { PackingInlineBanner } from '../packing-inline-banner/packing-inline-banner';
import { CONDENSED_BUTTON_FONT_FIX } from '../../panel-button-style';
import { messages } from '../../messages';
import cardStyle from '../../packing-card.module.css';

type PackingValidationWarningProps = {
  issueType: BlockingValidationErrorType;
  onRetry: () => void;
};

const WARNING_MESSAGES: Record<BlockingValidationErrorType, MessageDescriptor> = {
  [ValidationErrorType.NO_PACKAGES]: messages.missingCustomPackagesWarning,
  [ValidationErrorType.MISSING_DIMENSIONS]: messages.missingItemDimensionsWarning,
  [ValidationErrorType.MISSING_WEIGHT]: messages.missingItemWeightWarning,
  [ValidationErrorType.MISSING_ITEM_NAME]: messages.missingItemNameWarning,
};

export const PackingValidationWarning = ({ issueType, onRetry }: PackingValidationWarningProps) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const handleUpdatePackages = () => navigate(getPackagesCustomSettingsPath());

  const isMissingPackages = issueType === ValidationErrorType.NO_PACKAGES;

  return (
    <FlexRow className={classnames(cardStyle.card, cardStyle.cardAnimated, cardStyle.cardMargin)} gap={Gap.Space8}>
      <div className={cardStyle.cardIconWrapper}>
        <FontAwesomeIcon className={cardStyle.cardIcon} icon={faSparklesSolid} />
      </div>
      <FlexColumn className={cardStyle.cardBody}>
        <FlexRow className={cardStyle.cardHeadline}>
          <Text className={cardStyle.cardTitle} textType={TextType.Label} value={messages.panelTitle} />
          <Button onClick={onRetry} size='condensed' style={CONDENSED_BUTTON_FONT_FIX} variant='tertiary'>
            {intl.formatMessage(messages.retry)}
          </Button>
        </FlexRow>
        <PackingInlineBanner
          status='warning'
          description={intl.formatMessage(WARNING_MESSAGES[issueType])}
          actions={
            isMissingPackages ? (
              <Button onClick={handleUpdatePackages} size='condensed' style={CONDENSED_BUTTON_FONT_FIX} variant='tertiary'>
                {intl.formatMessage(messages.updatePackages)}
              </Button>
            ) : undefined
          }
        />
      </FlexColumn>
    </FlexRow>
  );
};
